
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { AuditHistoryEntry } from "../../../backend/backendapi";
import am4lang_en_US from "@amcharts/amcharts4/lang/en_US";
import am4lang_de_DE from "@amcharts/amcharts4/lang/de_DE";
import am4lang_sl_SL from "@amcharts/amcharts4/lang/sl_SL";
import am4lang_hu_HU from "@amcharts/amcharts4/lang/hu_HU";
import am4lang_hr_HR from "@amcharts/amcharts4/lang/hr_HR";

am4core.useTheme(am4themes_animated);
am4core.options.commercialLicense = true;

const locales = { en: am4lang_en_US, de: am4lang_de_DE, sl: am4lang_sl_SL, hu: am4lang_hu_HU, hr: am4lang_hr_HR }

@Component({
  name: 'LineChart',
})
export default class LineChart extends Vue {
  @Prop({ required: true })
  entries!: AuditHistoryEntry[];

  chart: any;

  mounted(): void {
    this.disposeChart();
    this.initChart();
  }

  updated(): void {
    this.disposeChart();
    this.initChart();
  }

  beforeDestroy(): void {
    this.disposeChart();
  }

  private initChart(): void {
    // Create chart instance
    this.chart = am4core.create(this.$refs.container as HTMLElement, am4charts.XYChart);
    this.chart.cursor = new am4charts.XYCursor();
    this.chart.data = this.entries;
    this.chart.language.locale = locales[this.$i18n.locale] ?? locales['en'];

    // legend
    let legendContainer = am4core.create(this.$refs.legend as HTMLElement, am4core.Container);
    legendContainer.width = am4core.percent(100);
    // legendContainer.height = am4core.percent(10);
    this.chart.legend = new am4charts.Legend();
    this.chart.legend.parent = legendContainer;
    this.chart.legend.zIndex = 100;
    this.chart.legend.align = "right";
    this.chart.legend.valign = "center";
    this.chart.legend.useDefaultMarker = true;

    // axis + series
    const xAxis = this.createXAxis('black');
    const yAxis = this.createYAxis('black');
    this.createSeries(this.$t('dashboard.auditHistory.chart.plannedNew').toString(), '#33cc66', 'plannedTriggered', xAxis, yAxis);
    this.createSeries(this.$t('dashboard.auditHistory.chart.plannedDone').toString(), '#0066ff', 'plannedDone', xAxis, yAxis);
    this.createSeries(this.$t('dashboard.auditHistory.chart.unplannedNew').toString(), '#ff3300', 'unplannedTriggered', xAxis, yAxis);
    this.createSeries(this.$t('dashboard.auditHistory.chart.unplannedDone').toString(), '#ff6633', 'unplannedDone', xAxis, yAxis);
  }

  private createXAxis(color: string): any {
    let axis = this.chart.xAxes.push(new am4charts.DateAxis());
    axis.tooltipDateFormat = this.$t('dateFormatString').toString();
    axis.renderer.grid.template.location = 0;
    axis.renderer.grid.template.strokeOpacity = 0.07;
    axis.renderer.labels.template.fill = am4core.color(color);
    return axis;
  }

  private createYAxis(color: string): any {
    let axis = this.chart.yAxes.push(new am4charts.ValueAxis());
    // axis.title.text = unit;
    axis.title.fill = am4core.color(color);
    axis.title.rotation = 0;
    axis.title.align = "center";
    axis.title.valign = "top";
    axis.title.dy = -40;
    axis.title.dx = 40;
    axis.title.fontWeight = 600;

    axis.tooltip.disabled = true;

    axis.renderer.grid.template.strokeDasharray = "2,3";
    axis.renderer.labels.template.fill = am4core.color(color);
    axis.renderer.minWidth = 60;
    axis.renderer.grid.template.strokeOpacity = 0.07;

    return axis;
  }

  private createSeries(label: string, color: string, valuePropName: string, valueAxis, dateAxis): any {
    let series = this.chart.series.push(new am4charts.LineSeries());
    series.name = label;
    series.dataFields.dateX = 'date';
    series.dataFields.valueY = valuePropName;
    // series.yAxis = valueAxis;
    // series.xAxis = dateAxis;
    series.tooltipText = label + ': {valueY.value}';
    series.fill = am4core.color(color);
    series.stroke = am4core.color(color);
    return series;
  }

  private disposeChart(): void {
    if (this.chart !== undefined)
      this.chart.dispose();
  }

  @Watch('entries')
  private onPropChanged() {
    this.disposeChart();
    this.initChart();
  }
}
